import React, { useState, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import supabase from '../../../supabase/supabase';

const { FiUserPlus, FiFileText, FiAlertCircle, FiX, FiCheck, FiSearch } = FiIcons;

export default function InvestigatorHandoffModal({ isOpen, onClose, report, onComplete }) {
  const [step, setStep] = useState(1);
  const [investigators, setInvestigators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState('');
  const [formData, setFormData] = useState({
    notes: '',
    scope: '',
    priority: 'standard'
  });

  useEffect(() => {
    if (isOpen) {
      fetchInvestigators();
    }
  }, [isOpen]);

  const fetchInvestigators = async () => {
    try {
      const { data, error } = await supabase
        .from('utredere')
        .select('*')
        .eq('active', true);
      
      if (error) throw error;
      setInvestigators(data || []);
    } catch (err) {
      console.error('Error fetching investigators:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleHandoff = async () => {
    try {
      setLoading(true);
      const investigator = investigators.find(i => i.id === selectedId);

      // 1. Create Assignment
      const { error: assignError } = await supabase
        .from('utreder_assignments')
        .insert([{
          report_id: report.id,
          utreder_id: selectedId,
          handoff_notes: formData.notes,
          scope_description: formData.scope,
          priority: formData.priority,
          status: 'assigned',
          handoff_date: new Date().toISOString()
        }]);

      if (assignError) throw assignError;

      // 2. Update Report Status
      const newHistory = [
        ...(report.history || []),
        {
          date: new Date().toISOString(),
          action: 'Overført til ekstern utreder',
          user: 'System Admin',
          details: `Sak overført til ${investigator.full_name}.`
        }
      ];

      const { error: updateError } = await supabase
        .from('reports')
        .update({
          status: 'investigating',
          history: newHistory
        })
        .eq('id', report.id);

      if (updateError) throw updateError;

      onComplete();
      onClose();
    } catch (err) {
      alert('Feil ved overføring: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const filtered = investigators.filter(i => 
    i.full_name.toLowerCase().includes(search.toLowerCase()) ||
    (i.company_name && i.company_name.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-2xl overflow-hidden shadow-2xl">
        <div className="p-6 border-b border-slate-800 flex justify-between items-center bg-slate-900/50">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <SafeIcon icon={FiUserPlus} className="text-blue-400" />
              Overlevering til utreder
            </h2>
            <p className="text-sm text-slate-400 mt-1">Velg en ekstern part for saksbehandling</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-800 rounded-lg text-slate-400 transition-colors">
            <SafeIcon icon={FiX} />
          </button>
        </div>

        <div className="p-8 max-h-[70vh] overflow-y-auto">
          {step === 1 ? (
            <div className="space-y-6">
              <div className="relative">
                <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  type="text"
                  placeholder="Søk etter navn eller selskap..."
                  className="w-full bg-slate-800 border-slate-700 rounded-xl py-3 pl-10 pr-4 text-white focus:ring-2 focus:ring-blue-500 transition-all outline-none"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              <div className="grid gap-3">
                {loading ? (
                  <div className="py-12 text-center text-slate-400">Laster utredere...</div>
                ) : filtered.length === 0 ? (
                  <div className="py-12 text-center text-slate-400">Ingen utredere funnet</div>
                ) : (
                  filtered.map(i => (
                    <button
                      key={i.id}
                      onClick={() => setSelectedId(i.id)}
                      className={`flex items-center gap-4 p-4 rounded-xl border transition-all text-left ${
                        selectedId === i.id 
                          ? 'bg-blue-500/10 border-blue-500 ring-1 ring-blue-500' 
                          : 'bg-slate-800/50 border-slate-700 hover:border-slate-500'
                      }`}
                    >
                      <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-400 font-bold text-lg">
                        {i.full_name[0]}
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold text-white">{i.full_name}</div>
                        <div className="text-sm text-slate-400">{i.company_name || 'Selvstendig'}</div>
                      </div>
                      {selectedId === i.id && <SafeIcon icon={FiCheck} className="text-blue-400" />}
                    </button>
                  ))
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Prioritet</label>
                <div className="grid grid-cols-3 gap-3">
                  {['lav', 'standard', 'høy'].map(p => (
                    <button
                      key={p}
                      onClick={() => setFormData(prev => ({ ...prev, priority: p }))}
                      className={`py-2 px-4 rounded-lg border text-sm font-medium capitalize transition-all ${
                        formData.priority === p 
                          ? 'bg-blue-500 border-blue-400 text-white shadow-lg shadow-blue-500/20' 
                          : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600'
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Hva skal utredes? (Scope)</label>
                <textarea
                  className="w-full bg-slate-800 border-slate-700 rounded-xl p-4 text-white min-h-[100px] focus:ring-2 focus:ring-blue-500 outline-none"
                  placeholder="Beskriv spesifikt hva utreder skal vurdere..."
                  value={formData.scope}
                  onChange={(e) => setFormData(prev => ({ ...prev, scope: e.target.value }))}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Overføringsnotater</label>
                <textarea
                  className="w-full bg-slate-800 border-slate-700 rounded-xl p-4 text-white min-h-[100px] focus:ring-2 focus:ring-blue-500 outline-none"
                  placeholder="Bakgrunnsinformasjon, interne vurderinger eller andre viktige detaljer..."
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                />
              </div>

              <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl flex gap-3">
                <SafeIcon icon={FiAlertCircle} className="text-blue-400 shrink-0 mt-0.5" />
                <p className="text-sm text-blue-200/80">
                  Ved overføring vil utrederen få tilgang til sakens dokumenter og historikk. 
                  Saken vil bli låst for endringer fra bedriftens side mens utredningen pågår.
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-slate-800 bg-slate-900/50 flex justify-between gap-3">
          {step === 2 && (
            <button
              onClick={() => setStep(1)}
              className="px-6 py-2.5 rounded-xl border border-slate-700 text-white font-medium hover:bg-slate-800 transition-all"
            >
              Tilbake
            </button>
          )}
          <div className="flex-1" />
          <button
            onClick={step === 1 ? () => setStep(2) : handleHandoff}
            disabled={step === 1 && !selectedId}
            className={`px-8 py-2.5 rounded-xl font-bold transition-all flex items-center gap-2 ${
              step === 1 && !selectedId
                ? 'bg-slate-800 text-slate-500 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-500/25 active:scale-[0.98]'
            }`}
          >
            {step === 1 ? 'Neste' : loading ? 'Overfører...' : 'Bekreft overlevering'}
          </button>
        </div>
      </div>
    </div>
  );
}